// components/artifacts/charts/ScatterChartComponent.tsx
import React from "react";
import { 
  ScatterChart, 
  Scatter, 
  CartesianGrid, 
  XAxis, 
  YAxis, 
  Cell,
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { GraphData } from "@/types/Artifact";
import { formatNumber } from "@/lib/utils";

interface ScatterChartComponentProps {
  data: GraphData;
  config: Record<string, any>;
  colors: string[];
  id?: string;
}

// Custom axis tick formatter that includes units
const formatAxisTick = (value: any, unit?: string) => {
  if (!unit) return value;
  
  switch (unit) {
    case '$':
    case '€':
    case '£':
    case '¥':
      return `${unit}${value}`;
    case '%':
      return `${value}${unit}`;
    default:
      return `${value} ${unit}`;
  }
};

// Custom tooltip formatter for scatter plots
const formatTooltipValue = (value: any, unit?: string) => {
  let y = value
  //if value is number, convert to string
  if (typeof value === 'number') {
    y = formatNumber(y);
  } 

  if (!unit) return value;
  
  // Handle different unit types
  switch (unit) {
    case '$':
    case '€':
    case '£':
    case '¥':
      return `${unit}${y}`;
    case '%':
      return `${y}${unit}`;
    default:
      return `${y} ${unit}`;
  }
};

export const ScatterChartComponent: React.FC<ScatterChartComponentProps> = React.memo(({
  data,
  config,
  colors,
  id,
}) => {
  const xAxisKey = data.xAxis;
  const yAxisKey = data.yAxis;
  const sizeKey = data.sizeKey || "size";
  const isBubble = data.type === "bubble";
  const { xAxisUnit, yAxisUnit } = data;

  if (!xAxisKey || !yAxisKey) {
    return (
      <div className="p-4 text-red-500" id={id}>
        Scatter plot requires xAxis and yAxis properties
      </div>
    );
  }

  // Custom shape component for bubble chart
  const renderCustomShape = (props: any) => {
    const { cx, cy, payload } = props;
    const bubbleSize = isBubble && payload[sizeKey] 
      ? Math.sqrt(payload[sizeKey]) * 2 
      : 6;
    
    return (
      <circle
        cx={cx}
        cy={cy}
        r={bubbleSize}
        fill={colors[0]}
        fillOpacity={0.6}
        stroke={colors[0]}
        strokeWidth={1}
      />
    );
  };

  return (
    <div className="p-4" id={id}>
      {/* Display unit information if available */}
      {/* {(xAxisUnit || yAxisUnit) && (
        <div className="mb-2 text-sm text-muted-foreground">
          {xAxisUnit && (
            <span className="mr-4">
              X-axis ({xAxisKey}): {xAxisUnit}
            </span>
          )}
          {yAxisUnit && (
            <span>
              Y-axis ({yAxisKey}): {yAxisUnit}
            </span>
          )}
        </div>
      )} */}
      
      <ChartContainer
        config={config}
        className="max-h-[calc(100vh-10rem)] min-h-[400px] w-full"
      >
        <ScatterChart accessibilityLayer>
          <CartesianGrid vertical={false} />
          <XAxis 
            type="number" 
            dataKey={xAxisKey} 
            name={xAxisKey}
            tickLine={false}
            tickMargin={10}
            axisLine={false}
            tickFormatter={(value) => formatAxisTick(value, xAxisUnit)}
          />
          <YAxis 
            type="number" 
            dataKey={yAxisKey} 
            name={yAxisKey}
            tickLine={false}
            tickMargin={10}
            axisLine={false}
            tickFormatter={(value) => formatAxisTick(value, yAxisUnit)}
          />
          <ChartTooltip 
            content={<ChartTooltipContent 
              valueFormatter={(value) => formatTooltipValue(value, yAxisUnit)}
            />} 
            cursor={{ strokeDasharray: "3 3" }} 
          />
          <ChartLegend content={<ChartLegendContent />} />
          <Scatter
            name={`${xAxisKey} vs ${yAxisKey}`}
            data={data.data}
            fill={colors[0]}
            shape={isBubble ? renderCustomShape : "circle"}
          >
            {!isBubble && data.data.map((_, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={colors[0]}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ChartContainer>
    </div>
  );
});